
import React, { useState, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import ImageUploader from '../ImageUploader';
import IngredientsSelector from '../IngredientsSelector';
import Icon from '../Icon';
import Spinner from '../Spinner';
import { fileToBase64, analyzeImageStyle } from '../../services/geminiService';

interface MatchMakerToolProps {
    onGenerate: (cost: number, quality: 'standard' | 'hd', promptBuilder: (onStatusUpdate?: (msg: string) => void) => Promise<{ prompt: string, images?: any[] }>) => Promise<void>;
    isProcessing: boolean;
}

const TARGET_OPTIONS = [
    'Earrings', 'Pendant', 'Ring', 'Necklace', 
    'Haaram', 'Bangle', 'Bracelet', 'Mangalsutra', 
    'Nose Pin', 'Maang Tikka', 'Waist Belt', 'Armlet'
];

const MatchMakerTool: React.FC<MatchMakerToolProps> = ({ onGenerate, isProcessing }) => {
    const { t } = useLanguage();
    const uploaderRef = useRef<{ clearFiles: () => void }>(null);
    
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    // Changed to array for multi-selection
    const [outputTargets, setOutputTargets] = useState<string[]>(['Earrings']);
    
    const [selectedMetal, setSelectedMetal] = useState('Gold');
    const [selectedFinish, setSelectedFinish] = useState('Polished');
    const [selectedProcess, setSelectedProcess] = useState('None');
    const [selectedStyle, setSelectedStyle] = useState('None');
    const [selectedGemstones, setSelectedGemstones] = useState<string[]>(['None']);

    const toggleTarget = (target: string) => {
        setOutputTargets(prev => {
            if (prev.includes(target)) {
                // Prevent deselecting the last item
                if (prev.length === 1) return prev;
                return prev.filter(t => t !== target);
            } else {
                return [...prev, target];
            }
        });
    };

    const handleGenerate = async (quality: 'standard' | 'hd') => {
        if (uploadedFiles.length === 0) return;
        // Cost Adjustment for Profit Margin:
        // Standard: 20 Swarnas (~68% Margin)
        // HD: 60 Swarnas (~79% Margin)
        const cost = quality === 'standard' ? 20 : 60;

        await onGenerate(cost, quality, async (onStatusUpdate) => {
            const file = uploadedFiles[0];
            const base64Data = await fileToBase64(file);
            const imagePart = { inlineData: { mimeType: file.type, data: base64Data } };
            
            let styleDesc = "matching jewelry style";
            // Use the status update to inform user about the analysis phase
            if (onStatusUpdate) onStatusUpdate("Analyzing reference style...");
            styleDesc = await analyzeImageStyle(imagePart, onStatusUpdate);
            
            const gemstones = selectedGemstones.includes('None') ? 'No additional gemstones' : selectedGemstones.join(' and ');
            const styleContext = selectedStyle !== 'None' ? `Specialty Style: ${selectedStyle}` : '';
            
            // Construct target string
            const targetString = outputTargets.join(', ');

            // Define Process Aesthetics
            let processDescription = "";
            switch (selectedProcess) {
                case 'Handmade':
                    processDescription = "Craftsmanship: Traditional Handmade. Construction involves pressing and manually welding several pieces together. Look for matte/antique finishes and organic artisan textures distinctive of manual assembly.";
                    break;
                case 'CNC/Laser':
                    processDescription = "Craftsmanship: CNC/Laser Precision. Involves machine-cutting and detailed engraving art. Sharp 90-degree edges, flat planes, precise geometric patterns, and industrial perfection.";
                    break;
                case 'Navishi':
                    processDescription = "Craftsmanship: Navishi (Nakshi) Work. Deep relief hand-repoussé. Intricate mythological or floral motifs embossed on gold sheet using traditional temple techniques.";
                    break;
                case 'CAD/Casting':
                    processDescription = "Craftsmanship: 3D CAD Sculpted & Cast. High-precision digital sculpting allowing for fluid geometry and complex volumes. Features advanced digital stone setting, smooth fillets, and perfect symmetry.";
                    break;
                default:
                    processDescription = "";
            }

            // Enhanced Prompt for Sets
            const prompt = `
            Task: Design a complementary jewelry set containing: ${targetString} to match the provided reference image.
            
            Reference Analysis: ${styleDesc}
            
            Target Specifications:
            - Items in Set: ${targetString}
            - Layout: Arrange all items (${targetString}) artistically in a single frame (Flat lay or Display Stand).
            - Base Metal: ${selectedMetal}
            - Surface Finish: ${selectedFinish}
            - ${styleContext}
            - ${processDescription}
            - Gemstones: ${gemstones}
            
            Design Goal: Create a cohesive matching set. All pieces must share the exact same design language, motifs, patterns, and visual weight as the reference.
            `;

            return { prompt, images: [imagePart] };
        });
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="space-y-3">
                <label className="text-xs uppercase font-black tracking-widest text-slate-400 pl-1">Reference Image</label>
                <ImageUploader ref={uploaderRef} onFilesChange={setUploadedFiles} disabled={isProcessing} multiple={false} />
            </div>
            
            <div className="space-y-3">
                <div className="flex justify-between items-center px-1">
                    <label className="text-xs uppercase font-black tracking-widest text-slate-400">{t('lblOutputTarget')}</label>
                    <span className="text-[10px] text-slate-400 italic">Select multiple for a set</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {TARGET_OPTIONS.map(opt => {
                        const isSelected = outputTargets.includes(opt);
                        return (
                            <button 
                                key={opt} 
                                onClick={() => toggleTarget(opt)} 
                                className={`h-10 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all border flex items-center justify-center gap-1
                                ${isSelected 
                                    ? 'bg-slate-900 text-white dark:bg-white dark:text-black border-transparent shadow-md scale-[1.02] ring-2 ring-offset-1 ring-apple-gold dark:ring-offset-black' 
                                    : 'bg-white dark:bg-white/5 border-black/5 dark:border-white/5 text-slate-500 hover:bg-slate-50 dark:hover:bg-white/10'}`}
                            >
                                {isSelected && <Icon name="check" className="w-3 h-3" />}
                                {opt}
                            </button>
                        );
                    })}
                </div>
            </div>

            <IngredientsSelector 
                selectedMetal={selectedMetal} setSelectedMetal={setSelectedMetal}
                selectedFinish={selectedFinish} setSelectedFinish={setSelectedFinish}
                selectedProcess={selectedProcess} setSelectedProcess={setSelectedProcess}
                selectedStyle={selectedStyle} setSelectedStyle={setSelectedStyle}
                selectedGemstones={selectedGemstones} setSelectedGemstones={setSelectedGemstones}
            />
            
            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-black/5 dark:border-white/5">
                <button 
                    onClick={() => handleGenerate('standard')} 
                    disabled={isProcessing || uploadedFiles.length === 0} 
                    className="flex flex-col items-center justify-center py-4 rounded-2xl font-bold transition-all active:scale-95 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 disabled:opacity-50 group"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                            <div className="flex items-center gap-2 mb-1">
                                <Icon name="sparkles" className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-white" />
                                <span className="text-sm">Standard</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-40">20 Swarnas</span>
                        </>
                    )}
                </button>

                <button 
                    onClick={() => handleGenerate('hd')} 
                    disabled={isProcessing || uploadedFiles.length === 0} 
                    className="flex flex-col items-center justify-center py-4 rounded-2xl font-bold transition-all active:scale-95 bg-apple-gold text-black disabled:opacity-50 shadow-lg shadow-apple-gold/20 hover:shadow-apple-gold/40 hover:scale-[1.02]"
                >
                    {isProcessing ? <Spinner /> : (
                        <>
                            <div className="flex items-center gap-2 mb-1">
                                <Icon name="diamond" className="w-4 h-4 text-black" />
                                <span className="text-sm">HD Premium</span>
                            </div>
                            <span className="text-xs uppercase font-black tracking-widest opacity-60">60 Swarnas</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default MatchMakerTool;
